import React, { useEffect, useState } from 'react';
import Stepper from './Stepper';
import StepIntro from './StepIntro';
import StepHousingType from './StepHousingType';
import StepPersonalInfo from './StepPersonalInfo';
import StepAddress from './StepAddress';
import StepPaymentInfo from './StepPaymentInfo';
import StatusQuery from './StatusQuery';
import PeriodBanner from './PeriodBanner';
import NotificationModal from './NotificationModal';
import KVKKModal from './KVKKModal';
import { STEPS, TOTAL_STEPS, VIEWS, APPLICATION_PERIOD, isApplicationPeriodOpen, FEATURES, WELCOME_ALERT } from './constants';
import Logo from './assets/logo.svg';
import { submitBasvuru } from './api';

const ApplicationForm = () => {
  const [view, setView] = useState(VIEWS.FORM);
  const [currentStep, setCurrentStep] = useState(STEPS.INTRO);
  const [kvkkAccepted, setKvkkAccepted] = useState(false);
  const [showKvkkModal, setShowKvkkModal] = useState(false);
  const [notification, setNotification] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Dönem bilgisi uygulama içinde sabittir; nihai kontrol sunucuda yapılır.
  const periodOpen = isApplicationPeriodOpen();

  const showCustomAlert = (title, message, type = 'info') => {
    setNotification({ isOpen: true, title, message, type });
  };

  useEffect(() => {
    // FEATURES.acilisHatirlatmasi = false ise modal hiç gösterilmez
    if (!FEATURES.acilisHatirlatmasi) return;

    const timer = setTimeout(
      () => showCustomAlert(WELCOME_ALERT.baslik, WELCOME_ALERT.mesaj),
      WELCOME_ALERT.gecikmeMs
    );
    return () => clearTimeout(timer);
  }, []);

  const [formData, setFormData] = useState({
    konutTipi: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    tcNo: '',
    phoneNumber: '',
    il: '',
    ilce: '',
    address: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Restrict tcNo and phoneNumber to only digits
    if (name === 'tcNo' || name === 'phoneNumber') {
      const numericValue = value.replace(/\D/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // İl değişince ilçe seçimi sıfırlanmalı
  const handleIlChange = (il) => {
    setFormData(prev => ({ ...prev, il, ilce: '' }));
  };

  const handleHousingTypeChange = (konutTipi) => {
    setFormData(prev => ({ ...prev, konutTipi }));
  };

  const handleNext = () => {
    // Validation before moving to the next step
    if (currentStep === STEPS.INTRO && !periodOpen) {
      showCustomAlert(
        'Başvuru Dönemi Dışında',
        `Başvurular yalnızca ${APPLICATION_PERIOD.text} tarihleri arasında alınmaktadır.`,
        'error'
      );
      return;
    }

    if (currentStep === STEPS.HOUSING_TYPE && !formData.konutTipi) {
      showCustomAlert('Eksik Bilgi', 'Lütfen başvurmak istediğiniz konut tipini seçiniz.', 'error');
      return;
    }

    if (currentStep === STEPS.PERSONAL_INFO) {
      if (!formData.firstName || !formData.lastName || !formData.birthDate || !formData.tcNo || !formData.phoneNumber) {
        showCustomAlert('Eksik Bilgi', 'Lütfen tüm kişisel bilgileri doldurunuz.', 'error');
        return;
      }
      if (formData.tcNo.length !== 11) {
        showCustomAlert('Hatalı Bilgi', 'T.C. Kimlik Numarası 11 haneli olmalıdır.', 'error');
        return;
      }
      if (formData.phoneNumber.length !== 10) {
        showCustomAlert('Hatalı Bilgi', 'Telefon numarası tam olarak 10 haneli olmalıdır.', 'error');
        return;
      }
      if (!formData.phoneNumber.startsWith('5')) {
        showCustomAlert('Hatalı Bilgi', 'Telefon numarası 5 ile başlamalıdır (Örn: 5051234567).', 'error');
        return;
      }
    }

    if (currentStep === STEPS.ADDRESS) {
      if (!formData.il) {
        showCustomAlert('Eksik Bilgi', 'Lütfen il seçiniz.', 'error');
        return;
      }
      if (!formData.ilce) {
        showCustomAlert('Eksik Bilgi', 'Lütfen ilçe seçiniz.', 'error');
        return;
      }
      if (!formData.address || formData.address.trim().length < 10) {
        showCustomAlert('Eksik Bilgi', 'Lütfen geçerli bir açık adres giriniz (en az 10 karakter).', 'error');
        return;
      }
    }

    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleViewChange = (nextView) => {
    setView(nextView);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!kvkkAccepted) {
      showCustomAlert('Hata', 'Lütfen KVKK metnini onaylayınız.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await submitBasvuru({
        konutTipi: formData.konutTipi,
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthDate: formData.birthDate,
        tcNo: formData.tcNo,
        phoneNumber: formData.phoneNumber,
        il: formData.il,
        ilce: formData.ilce,
        address: formData.address
      });

      // Handle successful response from backend
      if (data.success) {
        showCustomAlert(
          data.title || 'Başarılı',
          data.message || 'Başvurunuz alınmıştır.',
          'success'
        );

        // Show success state instead of resetting
        if (data.resetForm) {
          setResult(data);
          setIsSuccess(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        // Handle logic-level errors from backend (e.g. invalid TC)
        showCustomAlert(
          data.title || 'Hata',
          data.message || 'Bir hata oluştu.',
          'error'
        );
      }
    } catch (error) {
      // Handle HTTP errors or network issues
      const errorMessage = error.response?.data?.message || 'Sunucu ile iletişim kurulurken bir hata oluştu.';
      const errorTitle = error.response?.data?.title || 'Bağlantı Hatası';

      showCustomAlert(errorTitle, errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.INTRO:
        return <StepIntro periodText={APPLICATION_PERIOD.text} isOpen={periodOpen} />;
      case STEPS.HOUSING_TYPE:
        return (
          <StepHousingType
            formData={formData}
            onHousingTypeChange={handleHousingTypeChange}
          />
        );
      case STEPS.PERSONAL_INFO:
        return (
          <StepPersonalInfo
            formData={formData}
            handleInputChange={handleInputChange}
          />
        );
      case STEPS.ADDRESS:
        return (
          <StepAddress
            formData={formData}
            handleInputChange={handleInputChange}
            onIlChange={handleIlChange}
          />
        );
      case STEPS.PAYMENT:
        return (
          <StepPaymentInfo
            formData={formData}
            kvkkAccepted={kvkkAccepted}
            onKvkkChange={setKvkkAccepted}
            onKvkkModalOpen={() => setShowKvkkModal(true)}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            showAlert={showCustomAlert}
            isSuccess={isSuccess}
            result={result}
          />
        );
      default:
        return null;
    }
  };

  // Sorgulama kapalıysa her zaman başvuru ekranı gösterilir
  const isFormView = !FEATURES.basvuruSorgulama || view === VIEWS.FORM;
  const showNavigation = isFormView && !isSuccess;

  return (
    // Üstte çentik / dinamik ada, altta ana ekran çubuğu için güvenli alan payı bırakılır
    <div
      style={{
        paddingTop: 'calc(var(--safe-top) + 2rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 2rem)'
      }}
      className="min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={Logo} alt="Karatay Logo" className="w-full max-w-50 sm:max-w-sm mx-auto mb-7.5" />
        </div>

        {/* Başvuru / Sorgulama sekmeleri */}
        {FEATURES.basvuruSorgulama && (
          <div className="flex bg-white rounded-xl p-1 shadow-sm border border-gray-100 mb-6">
            <button
              type="button"
              onClick={() => handleViewChange(VIEWS.FORM)}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-200 ${isFormView ? 'bg-primary text-white shadow' : 'text-gray-600 hover:text-primary'
                }`}
            >
              Başvuru Yap
            </button>
            <button
              type="button"
              onClick={() => handleViewChange(VIEWS.STATUS)}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-200 ${!isFormView ? 'bg-primary text-white shadow' : 'text-gray-600 hover:text-primary'
                }`}
            >
              Başvuru Sorgula
            </button>
          </div>
        )}

        {/* Başvuru dönemi bilgilendirmesi */}
        <PeriodBanner periodText={APPLICATION_PERIOD.text} isOpen={periodOpen} />

        {/* Stepper */}
        {isFormView && <Stepper currentStep={currentStep} totalSteps={TOTAL_STEPS} />}

        {/* Main Form Card */}
        <div className={`bg-white rounded-2xl shadow-xl p-3 ${showNavigation ? 'mb-24' : 'mb-8'}`}>
          {isFormView ? renderStep() : <StatusQuery showAlert={showCustomAlert} />}
        </div>

        {/* Navigation Buttons - Fixed to Bottom - Hidden on Success */}
        {showNavigation && (
          <div
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
            className="fixed bottom-0 left-0 right-0 px-4 pt-4 md:px-6 md:pt-6 bg-white/80 backdrop-blur-md border-t border-gray-100 flex justify-center z-40"
          >
            <div className="max-w-4xl w-full flex justify-between gap-4">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 1 || isSubmitting}
                className={`
                  flex-1 flex items-center justify-center px-6 py-4 rounded-xl font-semibold transition-all duration-200
                  ${currentStep === 1 || isSubmitting
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-transparent'
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-primary hover:text-primary shadow-sm active:scale-[0.98]'
                  }
                `}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
                Geri
              </button>

              {currentStep < TOTAL_STEPS && (
                <button
                  onClick={handleNext}
                  disabled={isSubmitting}
                  className={`flex-1 flex items-center justify-center px-6 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl active:scale-[0.98] ${isSubmitting ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary-dark'
                    }`}
                >
                  {currentStep === 1 ? 'Başla' : 'İleri'}
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* KVKK Modal */}
      <KVKKModal
        isOpen={showKvkkModal}
        onClose={() => setShowKvkkModal(false)}
        onAccept={() => {
          setKvkkAccepted(true);
          setShowKvkkModal(false);
        }}
      />

      {/* Custom Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={() => setNotification(prev => ({ ...prev, isOpen: false }))}
        title={notification.title}
        message={notification.message}
        type={notification.type}
      />
    </div>
  );
};

export default ApplicationForm;
